/* eslint-disable react/no-array-index-key */
import { ShapeEditorActorContext } from 'app/agriculture/forms/components/ShapeEditor/ShapeEditorMachine';
import { ShapeEditorPolygonRenderer } from 'app/agriculture/forms/components/ShapeEditor/render/PolygonRenderer';
import { BillboardCollectionProvider } from 'app/agriculture/FarmMain/MapRenderer/context/BillboardCollectionContext';
import { PrimitiveCollectionProvider } from 'app/agriculture/FarmMain/MapRenderer/context/PrimitiveCollectionContext';
import { CesiumBillboard } from 'app/agriculture/FarmMain/MapRenderer/components/CesiumBillboard';
import {
    createEndPoint,
    createHalfHandle,
    createNextPoint,
    createPoint,
    createSelectedPoint,
} from 'app/agriculture/forms/components/ShapeEditor/icons/ShapeEditorGraphics';
import { computeHalfHandles } from 'app/agriculture/forms/components/ShapeEditor/utils';
import { useMemo } from 'react';
import { Cartesian3 } from 'cesium';

const getCurrentEndpoint = (
    mode: 'LineString' | 'Polygon',
    extendDirection: string | null,
    points: Cartesian3[],
) => {
    if (extendDirection === 'start' && points.length > 1) {
        return points[0];
    }

    if (extendDirection === 'end' && points.length > 1) {
        return points[points.length - 1];
    }

    // fallback for polygon creation (e.g. before it's closed)
    if (mode === 'Polygon' && points.length > 2) {
        return points[0];
    }

    if (mode === 'LineString' && points.length > 1) {
        return points[points.length - 1];
    }

    return null;
};

export function ShapeEditorRenderer() {
    const { extendDirection, mode, points, selectedPointIndex, nextPoint } =
        ShapeEditorActorContext.useSelector((state) => state.context);
    const isEditing = ShapeEditorActorContext.useSelector((state) => state.matches('Editing'));
    const endPoint = !isEditing ? getCurrentEndpoint(mode, extendDirection, points) : null;

    const halfHandles = useMemo(
        () => (isEditing ? computeHalfHandles(points) : []),
        [points, isEditing],
    );

    return (
        <PrimitiveCollectionProvider>
            <BillboardCollectionProvider>
                {endPoint == null ? null : (
                    <CesiumBillboard billboardOptions={createEndPoint(endPoint)} />
                )}
            </BillboardCollectionProvider>
            <BillboardCollectionProvider>
                {nextPoint == null ? null : (
                    <CesiumBillboard billboardOptions={createNextPoint(nextPoint)} />
                )}
            </BillboardCollectionProvider>
            <BillboardCollectionProvider>
                {points.map((point, index) =>
                    index === selectedPointIndex ? (
                        <CesiumBillboard
                            key={`point-${index}`}
                            billboardOptions={createSelectedPoint(point, index)}
                        />
                    ) : (
                        <CesiumBillboard
                            key={`point-${index}`}
                            billboardOptions={createPoint(point, index)}
                        />
                    ),
                )}
                {halfHandles.map((point, index) => (
                    <CesiumBillboard
                        key={`half-handle-${index}`}
                        billboardOptions={createHalfHandle(point, index + 1)}
                    />
                ))}
            </BillboardCollectionProvider>

            <ShapeEditorPolygonRenderer points={points} isEditing={isEditing} mode={mode} />
        </PrimitiveCollectionProvider>
    );
}
